import { Loader2, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Discussion } from "@/types";

interface DiscussionListProps {
  discussions: Discussion[];
  isLoading: boolean;
  onSelectDiscussion: (discussion: Discussion) => void;
  activeDiscussionId?: string;
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function DiscussionList({
  discussions,
  isLoading,
  onSelectDiscussion,
  activeDiscussionId,
}: DiscussionListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (discussions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <MessageSquare className="h-12 w-12 mb-3 opacity-50" />
        <p className="text-sm">No discussions yet</p>
        <p className="text-xs mt-1">Start a new thread to discuss this contract</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {discussions.map((discussion) => (
        <button
          key={discussion.id}
          onClick={() => onSelectDiscussion(discussion)}
          className={cn(
            "w-full text-left p-4 border-b border-slate-700/50 hover:bg-slate-800/50 transition-colors",
            discussion.is_unread && "bg-slate-800/30",
            activeDiscussionId === discussion.id && "bg-slate-800/70"
          )}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className={cn(
                  "font-medium text-sm truncate",
                  discussion.is_unread ? "text-white" : "text-slate-200"
                )}>
                  {discussion.title}
                </h3>
                {discussion.is_unread && (
                  <span className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                )}
              </div>
              <p className="text-xs text-slate-400 mt-1">
                by {discussion.profiles?.full_name || "Unknown User"}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className="text-xs text-slate-500">
                {discussion.last_message_at
                  ? formatRelativeTime(discussion.last_message_at)
                  : formatRelativeTime(discussion.created_at)}
              </span>
              {discussion.message_count && discussion.message_count > 1 && (
                <span className="text-xs text-slate-400 bg-slate-700 px-1.5 py-0.5 rounded">
                  {discussion.message_count} messages
                </span>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
